import { useRef, useState } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { RiskWorkspace } from '@app/types'
import { useSocialEconomicDemographicData } from '../../../hooks/useSocialEconomicDemographicData'
import { useSocialRiskQueryParams } from '../../../hooks/useSocialRiskQueryParams'
import { useExportXlsx } from '../../../hooks/useExportXlsx'
import { useExportDomImage } from '../../../hooks/useExportDomImage'
import type { DamageState } from '../../../types/riskProfileSelected.types'
import type { PropertiesMetricOption } from '../../../types/riskProfileProperties.types'
import { ChartQueryState } from '../../ChartQueryState'
import { RiskProfileCard } from '../../RiskProfileCard'
import { SocialDemographicChart } from './SocialDemographicChart'
import { ChartExportActions } from '../../../chart/ChartExportActions'

const RESIDENTS_METRIC_OPTION: PropertiesMetricOption = {
  value: 'population',
  metric: 'population',
  type: 'total',
  label: 'Residents exposed',
}

export interface RiskBySocialEconomicDemographicWidgetProps {
  selectedWorkspace: RiskWorkspace | null
  areaGroup: string | null
  areas: string[]
  selectedHazardGroups: string[]
  damageStates: DamageState[]
}

export function SocialDemographicWidget({
  selectedWorkspace,
  areaGroup,
  areas,
  selectedHazardGroups,
  damageStates,
}: RiskBySocialEconomicDemographicWidgetProps) {
  const [demographicView, setDemographicView] = useState<'age' | 'ethnicity'>('age')

  const queryParams = useSocialRiskQueryParams({
    selectedWorkspace,
    areaGroup,
    areas,
    selectedHazardGroups,

    demographicView,
    damageStates: [],
  })

  const { request, query } = useSocialEconomicDemographicData(queryParams)
  const { exportXlsx, isExporting } = useExportXlsx(request, '/report/risk-profile/properties/xlsx')
  const chartContentRef = useRef<HTMLDivElement>(null)
  const handleExportImage = useExportDomImage(chartContentRef, 'residents-by-demographic')

  return (
    <RiskProfileCard
      title="Risk of exposure by demographic"
      onExport={
        <ChartExportActions
          onExportXlsx={exportXlsx}
          onExportImage={handleExportImage}
          isExportingXlsx={isExporting}
          xlsxDisabled={!request}
        />
      }
      className="min-h-96"
    >
      <Tabs
        value={demographicView}
        onValueChange={(value) => setDemographicView(value as 'age' | 'ethnicity')}
      >
        <TabsList className="h-7 p-0.5 mb-2">
          <TabsTrigger className="h-6 px-2 text-xs" value="age">
            Age
          </TabsTrigger>
          <TabsTrigger className="h-6 px-2 text-xs" value="ethnicity">
            Ethnicity
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <ChartQueryState query={query}>
        {(data) => (
          <SocialDemographicChart
            ref={chartContentRef}
            data={data}
            metricOption={RESIDENTS_METRIC_OPTION}
            entityNoun="residents"
          />
        )}
      </ChartQueryState>
    </RiskProfileCard>
  )
}
